import { useEffect, useState, useMemo, useCallback, useContext } from "react";
import { faChartLine, faArrowUp, faArrowDown, faCalculator, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, Modal } from "react-bootstrap";
import revenueApi from "@serie3/freelancesplaceapi/api/finance/revenue";
import BillView from "./modals/BillView";
import BillOut from "./BillOut";
import BillIn from "./BillIn";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import formatAmount from "./formatAmount";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const getContactName = (bill) => {
  if (bill.person) {
    const p = bill.person;
    return [p.vorname, p.name].filter(Boolean).join(" ");
  }
  if (bill.kunde) return bill.kunde.firmenname || "—";
  return "—";
};

const BillCell = ({ bill, direction, borderLeft, onClick, isEmpty, fmt }) => {
  const isOut = direction === "out";
  const color      = isOut ? "#1b5e20" : "#b71c1c";
  const accentBg   = isOut ? "#e6f4ea" : "#fdecea";
  const bg         = isOut ? "#f4fbf5" : "#fef6f6";
  const emptyBg    = "#f2f2f2";
  const border     = borderLeft ? { borderLeft: "2px solid #d8d8d8" } : {};
  const rowBorder  = { borderBottom: "1px solid #e4e4e4" };

  if (!bill) return <td style={{ padding: "10px 14px", background: isEmpty ? emptyBg : bg, ...rowBorder, ...border }} />;

  const netto  = bill.netto  || 0;
  const brutto = bill.brutto || 0;
  const tax    = brutto - netto;

  return (
    <td onClick={onClick} style={{ padding: "10px 14px", verticalAlign: "middle", background: bg, cursor: "pointer", ...rowBorder, ...border }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.87rem", color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            #{bill.rechnungs_nummer} · {getContactName(bill)}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#616161", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {bill.beschreibung}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ display: "inline-block", background: accentBg, borderRadius: "6px", padding: "3px 9px" }}>
            <span style={{ fontWeight: 800, fontSize: "0.95rem", color, fontVariantNumeric: "tabular-nums" }}>{fmt(brutto)} €</span>
          </div>
          <div style={{ fontSize: "0.72rem", color: "#757575", marginTop: "4px", fontVariantNumeric: "tabular-nums" }}>
            Net {fmt(netto)} · Tax {fmt(tax)}
          </div>
        </div>
      </div>
    </td>
  );
};

const StatPill = ({ label, value, color }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
    <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "rgba(255,255,255,0.6)" }}>{label}</span>
    <span style={{ fontSize: "0.9rem", fontWeight: 700, color: color || "#fff" }}>{value} €</span>
  </div>
);

const CalcRow = ({ label, value, bold, color, indent, borderTop }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "9px 0",
    borderTop: borderTop ? "2px solid #e0e0e0" : "1px solid #f4f4f4",
    paddingLeft: indent ? "16px" : "0",
  }}>
    <span style={{ fontSize: bold ? "0.9rem" : "0.82rem", fontWeight: bold ? 700 : 500, color: "#424242" }}>{label}</span>
    <span style={{ fontSize: bold ? "1rem" : "0.85rem", fontWeight: bold ? 800 : 600, color: color || "#212121", fontVariantNumeric: "tabular-nums" }}>
      {value} €
    </span>
  </div>
);

const Revenue = () => {
  const { settings } = useContext(SettingsContext);
  const locale = settings?.locale;
  const fmt = (n) => formatAmount(n, locale);

  const YEAR_KEY = "finance_year";
  const [year, setYear] = useState(
    parseInt(localStorage.getItem(YEAR_KEY) || new Date().getFullYear(), 10)
  );

  const updateYear = (y) => {
    localStorage.setItem(YEAR_KEY, y);
    setYear(y);
  };
  const [byPayment, setByPayment] = useState(false);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [showCalc, setShowCalc] = useState(false);
  const [monthCalc, setMonthCalc] = useState(null);
  const [viewBill, setViewBill] = useState(null); // { bill, direction }

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetch = byPayment ? revenueApi.getRevenueByPayment(year) : revenueApi.getRevenue(year);
    fetch.then((response) => {
      if (response && response.status === 200) {
        setIncoming(response.data.incoming || []);
        setOutgoing(response.data.outgoing || []);
      }
    });
  }, [year, byPayment]);

  const groupByMonth = useCallback((bills) => {
    const groups = {};
    bills.forEach((b) => {
      const dateStr = byPayment ? (b.paymentdate || b.datum) : b.datum;
      const m = new Date(dateStr).getMonth();
      if (!groups[m]) groups[m] = [];
      groups[m].push(b);
    });
    return groups;
  }, [byPayment]);

  const inByMonth = useMemo(() => groupByMonth(incoming), [incoming, groupByMonth]);
  const outByMonth = useMemo(() => groupByMonth(outgoing), [outgoing, groupByMonth]);

  const activeMonths = useMemo(() =>
    [...new Set([...Object.keys(inByMonth), ...Object.keys(outByMonth)])]
      .map(Number)
      .sort((a, b) => a - b),
    [inByMonth, outByMonth]
  );

  const { totalOutNetto, totalOutBrutto, totalOutTax } = useMemo(() => {
    const totalOutNetto  = outgoing.reduce((s, b) => s + (b.netto  || 0), 0);
    const totalOutBrutto = outgoing.reduce((s, b) => s + (b.brutto || 0), 0);
    return { totalOutNetto, totalOutBrutto, totalOutTax: totalOutBrutto - totalOutNetto };
  }, [outgoing]);

  const { totalInNetto, totalInBrutto, totalInTax } = useMemo(() => {
    const totalInNetto  = incoming.reduce((s, b) => s + (b.exclude_from_vat ? (b.brutto || 0) : (b.netto  || 0)), 0);
    const totalInBrutto = incoming.reduce((s, b) => s + (b.brutto || 0), 0);
    const totalInTax    = incoming.reduce((s, b) => s + (b.exclude_from_vat ? 0 : ((b.brutto || 0) - (b.netto || 0))), 0);
    return { totalInNetto, totalInBrutto, totalInTax };
  }, [incoming]);

  const totalProfit    = useMemo(() => totalOutBrutto - totalInBrutto, [totalOutBrutto, totalInBrutto]);
  const totalProfitNet = useMemo(() => totalOutNetto  - totalInNetto,  [totalOutNetto,  totalInNetto]);
  const isProfitTotal  = totalProfitNet >= 0;

  const monthCalcData = useMemo(() => {
    if (monthCalc === null) return null;
    const ob      = outByMonth[monthCalc] || [];
    const ib      = inByMonth[monthCalc]  || [];
    const oNetto  = ob.reduce((s, b) => s + (b.netto  || 0), 0);
    const oBrutto = ob.reduce((s, b) => s + (b.brutto || 0), 0);
    const iNetto  = ib.reduce((s, b) => s + (b.exclude_from_vat ? (b.brutto || 0) : (b.netto  || 0)), 0);
    const iBrutto = ib.reduce((s, b) => s + (b.brutto || 0), 0);
    const iTax    = ib.reduce((s, b) => s + (b.exclude_from_vat ? 0 : ((b.brutto || 0) - (b.netto || 0))), 0);
    return { oNetto, oBrutto, oTax: oBrutto - oNetto, iNetto, iBrutto, iTax, pl: oBrutto - iBrutto };
  }, [monthCalc, outByMonth, inByMonth]);

  return (
    <div style={{ padding: "24px" }}>

      {/* Bill view modal */}
      {viewBill && (
        <BillView show bill={viewBill.bill} setShow={() => setViewBill(null)} direction={viewBill.direction}>
          {viewBill.direction === "out"
            ? <BillOut bill={viewBill.bill} />
            : <BillIn bill={viewBill.bill} type="bills" />}
        </BillView>
      )}

      <style>{`.rev-modal .modal-content { background: transparent !important; border: none !important; overflow: hidden; border-radius: 16px !important; }`}</style>

      {/* Calculator Modal */}
      <Modal show={showCalc} onHide={() => setShowCalc(false)} centered size="md" dialogClassName="rev-modal">
        <div style={{ borderRadius: "16px", overflow: "hidden" }}>
          {/* Modal header */}
          <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#fff" }}>
              <FontAwesomeIcon icon={faCalculator} style={{ fontSize: "1.1rem" }} />
              <span style={{ fontWeight: 700, fontSize: "1rem" }}>Tax & Revenue Summary — {year}</span>
            </div>
            <button onClick={() => setShowCalc(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", color: "#fff", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Modal body */}
          <div style={{ background: "#fff", padding: "24px" }}>

            {/* Outgoing section */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#2e7d32", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                <FontAwesomeIcon icon={faArrowUp} /> Outgoing Bills
              </div>
              <CalcRow label="Net (Netto)" value={fmt(totalOutNetto)} />
              <CalcRow label="Tax (Steuer)" value={fmt(totalOutTax)} color="#9e9e9e" indent />
              <CalcRow label="Gross (Brutto)" value={fmt(totalOutBrutto)} bold color="#2e7d32" borderTop />
            </div>

            {/* Incoming section */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c62828", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                <FontAwesomeIcon icon={faArrowDown} /> Incoming Bills
              </div>
              <CalcRow label="Net (Netto)" value={fmt(totalInNetto)} />
              <CalcRow label="Tax (Steuer)" value={fmt(totalInTax)} color="#9e9e9e" indent />
              <CalcRow label="Gross (Brutto)" value={fmt(totalInBrutto)} bold color="#c62828" borderTop />
            </div>

            {/* Per month breakdown */}
            {activeMonths.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "8px" }}>
                  Monthly Breakdown
                </div>
                {activeMonths.map((mIdx) => {
                  const ob = outByMonth[mIdx] || [];
                  const ib = inByMonth[mIdx] || [];
                  const oB = ob.reduce((s, b) => s + (b.brutto || 0), 0);
                  const iB = ib.reduce((s, b) => s + (b.brutto || 0), 0);
                  const pl = oB - iB;
                  const pos = pl >= 0;
                  return (
                    <div key={mIdx} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f4f4f4", fontSize: "0.82rem" }}>
                      <span style={{ fontWeight: 600, color: "#424242", minWidth: "90px" }}>{MONTHS[mIdx]}</span>
                      <span style={{ color: "#2e7d32" }}>Out {fmt(oB)} €</span>
                      <span style={{ color: "#c62828" }}>In {fmt(iB)} €</span>
                      <span style={{ fontWeight: 700, color: pos ? "#2e7d32" : "#c62828" }}>{pos ? "+" : ""}{fmt(pl)} €</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total P&L */}
            <div style={{ background: isProfitTotal ? "linear-gradient(135deg, #e8f5e9, #c8e6c9)" : "linear-gradient(135deg, #ffebee, #ffcdd2)", borderRadius: "12px", padding: "16px 20px" }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: isProfitTotal ? "#2e7d32" : "#c62828", marginBottom: "4px" }}>
                Total Profit / Loss {year}
              </div>
              <div style={{ fontSize: "1.4rem", fontWeight: 900, color: isProfitTotal ? "#1b5e20" : "#b71c1c" }}>
                {isProfitTotal ? "+" : ""}{fmt(totalProfit)} €
              </div>
              <div style={{ fontSize: "0.75rem", color: "#616161", marginTop: "4px" }}>
                Net Tax (Outgoing − Incoming): {fmt(totalOutTax - totalInTax)} €
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Month Calculator Modal */}
      {monthCalc !== null && monthCalcData && (() => {
        const mIdx = monthCalc;
        const { oNetto, oBrutto, oTax, iNetto, iBrutto, iTax, pl } = monthCalcData;
        const pos = pl >= 0;
        return (
          <Modal show onHide={() => setMonthCalc(null)} centered size="md" dialogClassName="rev-modal">
            <div style={{ borderRadius: "16px", overflow: "hidden" }}>
              <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#fff" }}>
                  <FontAwesomeIcon icon={faCalculator} style={{ fontSize: "1.1rem" }} />
                  <span style={{ fontWeight: 700, fontSize: "1rem" }}>{MONTHS[mIdx]} {year} — Summary</span>
                </div>
                <button onClick={() => setMonthCalc(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", color: "#fff", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div style={{ background: "#fff", padding: "24px" }}>
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#2e7d32", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <FontAwesomeIcon icon={faArrowUp} /> Outgoing Bills
                  </div>
                  <CalcRow label="Net (Netto)"    value={fmt(oNetto)} />
                  <CalcRow label="Tax (Steuer)"   value={fmt(oTax)}    color="#9e9e9e" indent />
                  <CalcRow label="Gross (Brutto)" value={fmt(oBrutto)} bold color="#2e7d32" borderTop />
                </div>
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c62828", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <FontAwesomeIcon icon={faArrowDown} /> Incoming Bills
                  </div>
                  <CalcRow label="Net (Netto)"    value={fmt(iNetto)} />
                  <CalcRow label="Tax (Steuer)"   value={fmt(iTax)}    color="#9e9e9e" indent />
                  <CalcRow label="Gross (Brutto)" value={fmt(iBrutto)} bold color="#c62828" borderTop />
                </div>
                <div style={{ background: pos ? "linear-gradient(135deg, #e8f5e9, #c8e6c9)" : "linear-gradient(135deg, #ffebee, #ffcdd2)", borderRadius: "12px", padding: "16px 20px" }}>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: pos ? "#2e7d32" : "#c62828", marginBottom: "4px" }}>
                    Profit / Loss {MONTHS[mIdx]}
                  </div>
                  <div style={{ fontSize: "1.4rem", fontWeight: 900, color: pos ? "#1b5e20" : "#b71c1c" }}>
                    {pos ? "+" : ""}{fmt(pl)} €
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#616161", marginTop: "4px" }}>
                    Net Tax (Outgoing − Incoming): {fmt(oTax - iTax)} €
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)", borderRadius: "16px", padding: "24px 28px", color: "#fff", marginBottom: "24px", display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
        {/* Icon */}
        <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
          <FontAwesomeIcon icon={faChartLine} />
        </div>

        {/* Title + year */}
        <div style={{ minWidth: "120px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700 }}>Revenue</h2>
            <Dropdown onClick={(e) => e.stopPropagation()}>
              <Dropdown.Toggle as="button" bsPrefix="no-caret" style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 10px 2px 8px", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "6px", fontWeight: 700, fontSize: "22px", color: "rgba(255,255,255,0.85)", cursor: "pointer", lineHeight: 1.2 }}>
                {year}
                <span style={{ fontSize: "11px", marginLeft: "2px", opacity: 0.6, marginTop: "4px" }}>▾</span>
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", border: "1px solid #f0f0f0", padding: "6px", minWidth: "120px", marginTop: "4px" }}>
                {yearOptions.map((y) => (
                  <Dropdown.Item key={y} as="div" onClick={() => updateYear(y)} style={{ borderRadius: "7px", fontSize: "0.92rem", fontWeight: y === year ? 700 : 500, color: y === year ? "#1a73e8" : "#424242", background: y === year ? "#e8f0fe" : "transparent", padding: "8px 14px", cursor: "pointer" }}>
                    {y}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: "13px", opacity: 0.75 }}>Overview of your revenue and earnings</p>
        </div>

        {/* Outgoing + Incoming stats — centered */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "20px" }}>
          <div style={{ width: "1px", height: "48px", background: "rgba(255,255,255,0.2)", flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: "0.75rem", color: "#a5d6a7" }} />
              <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "rgba(255,255,255,0.7)" }}>Outgoing</span>
            </div>
            <div style={{ display: "flex", gap: "16px" }}>
              <StatPill label="Net"   value={fmt(totalOutNetto)} />
              <StatPill label="Tax"   value={fmt(totalOutTax)}   color="rgba(255,255,255,0.7)" />
              <StatPill label="Gross" value={fmt(totalOutBrutto)} color="#a5d6a7" />
            </div>
          </div>
          <div style={{ width: "1px", height: "48px", background: "rgba(255,255,255,0.2)", flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: "0.75rem", color: "#ef9a9a" }} />
              <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "rgba(255,255,255,0.7)" }}>Incoming</span>
            </div>
            <div style={{ display: "flex", gap: "16px" }}>
              <StatPill label="Net"   value={fmt(totalInNetto)} />
              <StatPill label="Tax"   value={fmt(totalInTax)}   color="rgba(255,255,255,0.7)" />
              <StatPill label="Gross" value={fmt(totalInBrutto)} color="#ef9a9a" />
            </div>
          </div>
          <div style={{ width: "1px", height: "48px", background: "rgba(255,255,255,0.2)", flexShrink: 0 }} />
        </div>

        {/* Calculator button + Profit/Loss */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <button
            onClick={() => setByPayment(!byPayment)}
            title={byPayment ? "Grouped by payment date" : "Grouped by invoice date"}
            style={{ height: "42px", padding: "0 14px", borderRadius: "10px", background: byPayment ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: byPayment ? "#1a73e8" : "rgba(255,255,255,0.85)", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}
          >
            {byPayment ? "Payment Date" : "Invoice Date"}
          </button>
          <button
            onClick={() => setShowCalc(true)}
            title="Open tax & revenue calculator"
            style={{ width: "42px", height: "42px", borderRadius: "10px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={faCalculator} />
          </button>

          <div style={{ padding: "12px 20px", borderRadius: "12px", background: isProfitTotal ? "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)" : "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)", boxShadow: isProfitTotal ? "0 4px 16px rgba(27,94,32,0.55)" : "0 4px 16px rgba(183,28,28,0.55)", textAlign: "center" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Profit / Loss</div>
            <div style={{ fontSize: "1.15rem", fontWeight: 900, color: "#fff", marginTop: "3px" }}>
              {isProfitTotal ? "+" : ""}{fmt(totalProfitNet)} €
            </div>
            <div style={{ marginTop: "4px", fontSize: "0.68rem", color: "rgba(255,255,255,0.65)", fontVariantNumeric: "tabular-nums" }}>
              Gross {totalProfit >= 0 ? "+" : ""}{fmt(totalProfit)} €
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "50%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ padding: "12px 14px", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2e7d32", background: "#f8fdf8", borderBottom: "2px solid #c8e6c9" }}>
                <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: "6px" }} />Outgoing
              </th>
              <th style={{ padding: "12px 14px", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#c62828", background: "#fff9f9", borderBottom: "2px solid #ffcdd2", borderLeft: "2px solid #e0e0e0" }}>
                <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: "6px" }} />Incoming
              </th>
            </tr>
          </thead>
          <tbody>
            {activeMonths.length === 0 ? (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", padding: "48px", color: "#bdbdbd", fontSize: "0.92rem" }}>
                  No revenue data for {year}.
                </td>
              </tr>
            ) : (
              activeMonths.map((monthIdx) => {
                const outBills = outByMonth[monthIdx] || [];
                const inBills  = inByMonth[monthIdx]  || [];
                const rowCount = Math.max(outBills.length, inBills.length);

                const outBrutto = outBills.reduce((s, b) => s + (b.brutto || 0), 0);
                const inBrutto  = inBills.reduce((s, b)  => s + (b.brutto || 0), 0);

                const monthProfit  = outBrutto - inBrutto;
                const isProfit     = monthProfit >= 0;
                const profitBg     = isProfit ? "#e8f5e9" : "#ffebee";
                const profitColor  = isProfit ? "#2e7d32" : "#c62828";
                const profitBorder = isProfit ? "#a5d6a7" : "#ef9a9a";

                return [
                  /* Spacer between months */
                  <tr key={`spacer-${monthIdx}`}>
                    <td colSpan={2} style={{ padding: "10px 0", background: "#f0f2f5" }} />
                  </tr>,

                  /* Month header row */
                  <tr key={`sep-${monthIdx}`}>
                    <td style={{ padding: "10px 14px", background: profitBg, borderTop: `3px solid ${profitBorder}`, verticalAlign: "middle" }}>
                      <span style={{ fontWeight: 800, fontSize: "0.88rem", color: "#1a73e8", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                        {MONTHS[monthIdx]}
                      </span>
                    </td>
                    <td
                      onClick={() => setMonthCalc(monthIdx)}
                      title={`Show ${MONTHS[monthIdx]} summary`}
                      style={{ padding: "10px 14px", background: profitBg, borderTop: `3px solid ${profitBorder}`, borderLeft: "2px solid #e0e0e0", verticalAlign: "middle", textAlign: "right", cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "5px" }}>
                        <FontAwesomeIcon icon={faCalculator} style={{ fontSize: "0.65rem", color: profitColor, opacity: 0.45 }} />
                        <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: profitColor, opacity: 0.75 }}>P&L</span>
                      </div>
                      <div style={{ fontSize: "0.95rem", fontWeight: 900, color: profitColor }}>
                        {isProfit ? "+" : ""}{fmt(monthProfit)} €
                      </div>
                    </td>
                  </tr>,

                  /* Bill rows */
                  ...Array.from({ length: rowCount }, (_, i) => {
                    const ob = outBills[i];
                    const ib = inBills[i];
                    return (
                      <tr key={`${monthIdx}-${i}`}>
                        <BillCell bill={ob} direction="out" isEmpty={outBills.length === 0} onClick={ob ? () => setViewBill({ bill: ob, direction: "out" }) : undefined} fmt={fmt} />
                        <BillCell bill={ib} direction="in"  isEmpty={inBills.length === 0} borderLeft onClick={ib ? () => setViewBill({ bill: ib, direction: "in" }) : undefined} fmt={fmt} />
                      </tr>
                    );
                  }),
                ];
              })
            )}
          </tbody>
          {activeMonths.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={2} style={{ padding: "14px 18px", background: isProfitTotal ? "linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)" : "linear-gradient(90deg, #b71c1c 0%, #c62828 100%)", textAlign: "right" }}>
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.75)", marginRight: "12px" }}>
                    Total Profit / Loss {year}
                  </span>
                  <span style={{ fontSize: "1.05rem", fontWeight: 800, color: "#fff" }}>
                    {isProfitTotal ? "+" : ""}{fmt(totalProfitNet)} €
                  </span>
                  <span style={{ fontSize: "0.78rem", fontWeight: 500, color: "rgba(255,255,255,0.6)", marginLeft: "8px" }}>
                    ({totalProfit >= 0 ? "+" : ""}{fmt(totalProfit)} € gross)
                  </span>
                  <span style={{
                    marginLeft: "20px", fontSize: "0.78rem", fontWeight: 700,
                    color: (totalOutTax - totalInTax) > 0 ? "#ff8a80" : "#b9f6ca",
                  }}>
                    Tax: {fmt(totalOutTax - totalInTax)} €
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default Revenue;
